/**
 * Deterministic 8-bit "trading card" stats for a collectible. Pure flavor —
 * no gameplay effect. Same (slug, rarity) always yields the same four stats
 * for every player, so two people comparing the same item see identical bars.
 * Rarity raises the floor so legendaries visibly roll juicier numbers.
 */

export const STAT_LABELS = ['BREW PWR', 'CAFFEINE', 'LUCK', 'CHARM'] as const;
export type StatKey = (typeof STAT_LABELS)[number];
export type Stats = Record<StatKey, number>;

type Rarity = 'common' | 'uncommon' | 'rare' | 'legendary';

// Per-stat floor by rarity — higher rarity, higher guaranteed bars.
const RARITY_FLOOR: Record<Rarity, number> = {
  common: 25, uncommon: 40, rare: 55, legendary: 70,
};
// Hash-distributed headroom above the floor (max stat = floor + SPREAD, ≤99).
const SPREAD = 29;

// Cheap deterministic 32-bit hash of a string, salted per stat so the four
// bars differ from each other for the same item.
function hash(str: string, salt: number): number {
  let h = (2166136261 ^ salt) >>> 0;
  for (let i = 0; i < str.length; i++) {
    h = Math.imul(h ^ str.charCodeAt(i), 16777619) >>> 0;
  }
  return h >>> 0;
}

export function deriveStats(slug: string, rarity: string): Stats {
  const floor = RARITY_FLOOR[(rarity as Rarity)] ?? RARITY_FLOOR.common;
  const stats = {} as Stats;
  STAT_LABELS.forEach((label, i) => {
    const bump = hash(slug, i + 1) % (SPREAD + 1); // 0..SPREAD
    stats[label] = Math.min(99, floor + bump);
  });
  return stats;
}
